import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";

/**
 * SONMI · The Ecology of Understanding — read-only view of
 * /app/memory/ECOLOGY_OF_UNDERSTANDING.md. Fetches the raw markdown
 * from the backend and renders it in the Observatory's visual tone.
 * The file remains the source of truth.
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function EcologyView() {
  const [markdown, setMarkdown] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/sonmi/docs/ecology`);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = await r.json();
        setMarkdown(data.markdown);
      } catch (e) {
        setError(String(e.message || e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div
      className="min-h-screen w-full"
      style={{ backgroundColor: "#FAFAF7", fontFamily: "'Outfit', sans-serif", color: "#44403c" }}
      data-testid="ecology-view"
    >
      <div className="absolute top-6 left-8 text-[10px] uppercase tracking-[0.18em] text-stone-400">
        SONMI · The Written Record · Ecology
      </div>
      <div className="absolute top-6 right-8">
        <Link
          to="/"
          className="text-[11px] italic text-stone-400 hover:text-stone-700 transition-colors"
          data-testid="ecology-back"
        >
          ← Atrium
        </Link>
      </div>

      <div className="max-w-2xl mx-auto px-8 pt-28 pb-24">
        {loading && (
          <div className="text-stone-400 italic text-[13px]" data-testid="ecology-loading">
            gathering…
          </div>
        )}
        {error && (
          <div className="text-red-400 text-[13px]" data-testid="ecology-error">
            {error}
          </div>
        )}
        {!loading && !error && (
          <article className="ecology-prose" data-testid="ecology-body">
            <ReactMarkdown>{markdown}</ReactMarkdown>
          </article>
        )}
      </div>

      <style>{`
        .ecology-prose h1 {
          font-family: 'Cormorant Garamond', serif;
          font-weight: 300;
          font-size: 2.4rem;
          letter-spacing: -0.01em;
          line-height: 1.15;
          color: #1c1917;
          margin: 0 0 1.5rem;
        }
        .ecology-prose h2 {
          font-family: 'Cormorant Garamond', serif;
          font-weight: 400;
          font-size: 1.55rem;
          letter-spacing: -0.005em;
          color: #292524;
          margin: 3rem 0 1rem;
        }
        .ecology-prose h3 {
          font-family: 'Cormorant Garamond', serif;
          font-weight: 400;
          font-size: 1.2rem;
          color: #292524;
          margin: 2rem 0 0.75rem;
        }
        .ecology-prose h4 {
          font-family: 'Cormorant Garamond', serif;
          font-weight: 400;
          font-size: 1.05rem;
          color: #292524;
          margin: 1.75rem 0 0.5rem;
        }
        .ecology-prose p {
          font-size: 1rem;
          line-height: 1.75;
          color: #44403c;
          margin: 0 0 1rem;
        }
        .ecology-prose em {
          font-style: italic;
        }
        .ecology-prose strong {
          font-weight: 500;
          color: #1c1917;
        }
        .ecology-prose blockquote {
          border-left: 2px solid #d6d3d1;
          padding: 0.25rem 0 0.25rem 1.25rem;
          margin: 1.5rem 0;
          font-style: italic;
          color: #57534e;
        }
        .ecology-prose blockquote p { margin: 0.35rem 0; }
        .ecology-prose ul, .ecology-prose ol {
          margin: 0.5rem 0 1.25rem;
          padding-left: 1.25rem;
        }
        .ecology-prose li {
          font-size: 1rem;
          line-height: 1.7;
          color: #44403c;
          margin: 0.25rem 0;
        }
        .ecology-prose hr {
          border: none;
          border-top: 1px solid #e7e5e4;
          margin: 3rem 0;
        }
        .ecology-prose code {
          font-family: ui-monospace, SFMono-Regular, monospace;
          font-size: 0.85em;
          background: #f5f5f4;
          padding: 0.05em 0.35em;
          border-radius: 3px;
          color: #57534e;
        }
      `}</style>
    </div>
  );
}
